(ns frontend.ux-overhaul.listing.generic.event
  (:require
   [cljs.reader :as edn]
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.completion :as completion]
   [common.localization :as loc]
   [common.opensearch.access :as os-access]
   [common.opensearch.facet-policy :as facet-policy]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.schema.data-types :as data-types]
   [common.schema.entities.pipeline :as pipeline-entity]
   [common.schema.listing-definition :as listing-definition]
   [common.schema.registry :as registry]
   [common.schema.system :as schema-system]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.local-storage :as ls]
   [frontend.state.current-session :as session]
   [ui.completion.model :as completion-model]
   [ui.components.date-picker.view :as date-picker]
   [ui.editors.simple-filters.event :as simple-filters-event]
   [ui.editors.simple-filters.model :as simple-filters-model]
   [ui.fx.toast :as toast]
   [ui.state.session.cofx :as session-cofx]
   [ui.view.grid-opensearch.event :as grid-event]
   [ui.view.grid-opensearch.model :as grid-model]
   [ui.view.kanban-opensearch.event :as kanban-event]
   [re-frame.core :as rf]
   [ui.fx.timeout :as timeout]
   [frontend.ux-overhaul.listing.generic.legacy-views :as legacy-views]
   [frontend.ux-overhaul.listing.generic.model :as model]
   [frontend.ux-overhaul.listing.generic.selection :as selection]))

(defn- listing-runtime
  [db listing-id]
  (get-in db (model/listing-path listing-id)))

(defn- callback-fx
  [runtime callback-key payload]
  (when-let [event-v (get-in runtime [:callbacks callback-key])]
    [[:dispatch (conj (vec event-v) payload)]]))

(defn- append-callback-fx
  [fx runtime callback-key payload]
  (if-let [cb (callback-fx runtime callback-key payload)]
    (conj (vec fx) cb)
    (vec fx)))

(defn- ls-path
  [listing-id & more]
  (into [(:db/id @session/account)
         (:db/id @session/tenant)
         :opensearch-listing-view-settings
         listing-id]
        more))

(defn saved-view-payload
  [view-config]
  (select-keys view-config [:columns :sort :group-by-field :row-group-by-field :row-group-sort-mode :kanban-column-sort-mode
                            :kanban-card-sort-mode :hide-empty-columns? :card]))

(defn- saved-view-record
  [view-config]
  {:schema-version 1
   :scope/type :user
   :scope/id (:db/id @session/account)
   :updated-at-ms (.now js/Date)
   :payload (saved-view-payload view-config)})

(defn- persist-view-config!
  [listing-id view-config]
  (ls/set-in (ls-path listing-id :view-config)
             (saved-view-payload view-config))
  (ls/set-in (ls-path listing-id :saved-view)
             (saved-view-record view-config)))

(defn- persist-view-mode!
  [listing-id view-mode]
  (ls/set-in (ls-path listing-id :view-mode) view-mode))

(defn- persist-filters!
  [listing-id filters-by-field-path]
  (ls/set-in (ls-path listing-id :filters) filters-by-field-path))

(defn- persist-active-view-id!
  [listing-id view-id]
  (ls/set-in (ls-path listing-id :active-view-id) view-id))

(defn- persist-view-settings?
  [db listing-id]
  (not (false? (get-in db (conj (model/params-path listing-id) :persist-view-settings?)))))

(defn- maybe-persist-view-config!
  [db listing-id view-config]
  (when (persist-view-settings? db listing-id)
    (persist-view-config! listing-id view-config)))

(defn- maybe-persist-view-mode!
  [db listing-id view-mode]
  (when (persist-view-settings? db listing-id)
    (persist-view-mode! listing-id view-mode)))

(defn- maybe-persist-filters!
  [db listing-id filters-by-field-path]
  (when (persist-view-settings? db listing-id)
    (persist-filters! listing-id filters-by-field-path)))

(defn- maybe-persist-active-view-id!
  [db listing-id view-id]
  (when (persist-view-settings? db listing-id)
    (persist-active-view-id! listing-id view-id)))

(defn- persisted-view-config
  [listing-id]
  (let [saved-view (ls/get-in (ls-path listing-id :saved-view))
        legacy-config (ls/get-in (ls-path listing-id :view-config))
        saved-payload (:payload saved-view)]
    (cond
      (map? saved-payload) saved-payload
      (map? legacy-config) legacy-config
      :else nil)))

(defn- persisted-view-mode
  [listing-id]
  (ls/get-in (ls-path listing-id :view-mode)))

(defn- persisted-filters
  [listing-id]
  (ls/get-in (ls-path listing-id :filters)))

(defn- persisted-active-view-id
  [listing-id]
  (ls/get-in (ls-path listing-id :active-view-id)))

(def named-view-schema-version 2)

(defn decode-saved-view-settings
  "`:named-view/settings` is a text-edn field: the backend stores it as a
   pr-str'd string and does not auto-decode it on sync, so callers must read
   it back explicitly. Accepts an already-decoded map too, defensively."
  [settings]
  (cond
    (map? settings) settings
    (string? settings) (try
                         (edn/read-string settings)
                         (catch :default _ nil))
    :else nil))

(defn- current-filters-by-field-path
  [db editor-id]
  (get-in db (conj (simple-filters-model/filters-path editor-id) :by-field-path) {}))

(def ^:private inject-persisted-grid-config
  "An interceptor that adds `:persisted-grid-config` to the coeffects: the config
   that the user saved for the grid of the listing of the event. The grid is not
   open in the board view, and the saved config tells its sort. The event is
   `[id {:listing-id ..}]`."
  (rf/->interceptor
   :id ::inject-persisted-grid-config
   :before (fn [context]
             (let [[_ {:keys [listing-id]}] (get-in context [:coeffects :event])]
               (assoc-in context
                         [:coeffects :persisted-grid-config]
                         (grid-event/persisted-config (model/grid-id listing-id)))))))

(defn- current-saved-view-settings
  "The settings of a view that saves the listing as it is now. The sort of the
   grid is part of them, when the config of the grid is known: from the open
   grid, or else from the config that the user saved."
  [{:keys [db listing-id editor-id persisted-grid-config]}]
  (let [runtime (listing-runtime db listing-id)
        grid-config (or (get-in db (grid-model/config-full-path (model/grid-id listing-id)))
                        persisted-grid-config)]
    (cond-> {:view-config (saved-view-payload (:view-config runtime))
             :view-mode   (get-in runtime [:view :mode])
             :filters     (current-filters-by-field-path db editor-id)}
      (map? grid-config)
      (assoc :grid {:sorted-by (grid-event/config-sorted-by grid-config)}))))

(defn new-saved-view-entity
  "The entity of a new saved view. `view-key` is the keyword that says which
   listing, or which scope of listings, the view belongs to. See
   `model/saved-view-key`."
  [{:keys [account-id name view-key settings]}]
  {:entity/type               :entity.type/named-view
   :named-view/account        account-id
   :named-view/name           name
   :named-view/view           view-key
   :named-view/settings       settings
   :named-view/schema-version named-view-schema-version})

(rf/reg-event-fx
 ::create-saved-view
 [(rf/inject-cofx ::session-cofx/session-context)
  inject-persisted-grid-config]
 (fn [{:keys [db session-context persisted-grid-config]} [_ {:keys [listing-id editor-id name scope]}]]
   (let [settings (current-saved-view-settings {:db                    db
                                                :listing-id            listing-id
                                                :editor-id             editor-id
                                                :persisted-grid-config persisted-grid-config})
         account-id (:db/id (:account session-context))
         type-listing-id (get-in (listing-runtime db listing-id) [:params :type-listing-id])]
     (entity-actions/*->create
      (new-saved-view-entity
       {:account-id account-id
        :name       name
        :view-key   (model/saved-view-key {:listing-id      listing-id
                                           :type-listing-id type-listing-id
                                           :scope           scope})
        :settings   settings})
      (fn [resp] (rf/dispatch [::save-view-succeeded {:listing-id listing-id :view-id (:db/id (:body resp))}]))
      (fn [_err] (rf/dispatch [::save-view-failed])))
     {})))

(rf/reg-event-fx
 ::save-view-succeeded
 (fn [{:keys [db]} [_ {:keys [listing-id view-id]}]]
   (let [updated-db (cond-> db
                      (and listing-id view-id)
                      (assoc-in (conj (model/views-path listing-id) :active-id) view-id))]
     (when (and listing-id view-id)
       (maybe-persist-active-view-id! updated-db listing-id view-id))
     {:db updated-db
      ::toast/success (loc/tr :saved-view-created)})))

(rf/reg-event-fx
 ::save-view-failed
 (fn [_ _]
   {::toast/error (loc/tr :saved-view-create-failed)}))

(rf/reg-event-fx
 ::update-saved-view-settings
 [inject-persisted-grid-config]
 (fn [{:keys [db persisted-grid-config]} [_ {:keys [view-id listing-id editor-id]}]]
   (let [settings (current-saved-view-settings {:db                    db
                                                :listing-id            listing-id
                                                :editor-id             editor-id
                                                :persisted-grid-config persisted-grid-config})]
     (entity-actions/*->entity-update!
      view-id
      {:named-view/settings settings}
      (fn [_resp] (rf/dispatch [::update-view-succeeded]))
      (fn [_err] (rf/dispatch [::update-view-failed])))
     {})))

(rf/reg-event-fx
 ::update-view-succeeded
 (fn [_ _]
   {::toast/success (loc/tr :saved-view-updated)}))

(rf/reg-event-fx
 ::update-view-failed
 (fn [_ _]
   {::toast/error (loc/tr :saved-view-update-failed)}))

(rf/reg-event-fx
 ::rename-saved-view
 (fn [_ [_ {:keys [view-id name]}]]
   (entity-actions/*->entity-update!
    view-id
    {:named-view/name name}
    (fn [_resp] (rf/dispatch [::rename-view-succeeded]))
    (fn [_err] (rf/dispatch [::rename-view-failed])))
   {}))

(rf/reg-event-fx
 ::rename-view-succeeded
 (fn [_ _]
   {::toast/success (loc/tr :saved-view-renamed)}))

(rf/reg-event-fx
 ::rename-view-failed
 (fn [_ _]
   {::toast/error (loc/tr :saved-view-rename-failed)}))

(rf/reg-event-fx
 ::delete-saved-view
 (fn [_ [_ {:keys [view-id listing-id]}]]
   (entity-actions/*->disable!
    view-id
    (fn [_resp] (rf/dispatch [::delete-view-succeeded {:listing-id listing-id :view-id view-id}]))
    (fn [_err] (rf/dispatch [::delete-view-failed])))
   {}))

(rf/reg-event-fx
 ::delete-view-succeeded
 (fn [{:keys [db]} [_ {:keys [listing-id view-id]}]]
   (let [active-id (get-in db (conj (model/views-path listing-id) :active-id))
         was-active? (and listing-id (= active-id view-id))
         updated-db (if was-active?
                      (update-in db (model/views-path listing-id) dissoc :active-id)
                      db)]
     (when was-active?
       (persist-active-view-id! listing-id nil))
     {:db updated-db
      ::toast/success (loc/tr :saved-view-deleted)})))

(rf/reg-event-fx
 ::delete-view-failed
 (fn [_ _]
   {::toast/error (loc/tr :saved-view-delete-failed)}))

(defn saved-view-fx
  "The effects that apply a view to a listing, in this order: the filters, the
   new context, the sort of the grid when the view has one, and the view mode. The
   sort comes before the mode. A grid that the mode opens is made after it and
   starts with the sort. `sorted-by` is nil when the view has no sort. A view
   with an empty sort clears the sort."
  [{:keys [listing-id editor-id view-mode filters sorted-by runtime]}]
  (let [{:keys [entity-type pipeline-id]} (:params runtime)]
    (cond-> [[:dispatch [::simple-filters-event/set-all-filters editor-id (or filters {})]]
             [:dispatch [::refresh-context listing-id]]]
      (some? sorted-by)
      (conj [:dispatch [::grid-event/apply-sorted-by
                        {:grid-id                (model/grid-id listing-id)
                         :sorted-by              sorted-by
                         :entity-type            entity-type
                         :pipeline-id            pipeline-id
                         :accessible-field-paths (mapv :field-path (get-in runtime [:context :accessible-fields]))}]])

      view-mode
      (conj [:dispatch [::set-view-mode listing-id view-mode]]))))

(defn view-settings
  "The settings that apply `named-view` to a listing: `:view-config`, `:view-mode`,
   `:filters` and `:grid`. A saved view has them as text, and a legacy view has the
   settings of its legacy grid. These are made again for the listing, with the labels
   of the filters, from the data of the session in `session-context` and the fields
   that the user can read, `accessible-field-paths`."
  [{:keys [named-view session-context accessible-field-paths]}]
  (if (:legacy-view? named-view)
    (legacy-views/legacy-view->settings
     {:settings               (:legacy-settings named-view)
      :accessible-field-paths accessible-field-paths
      :entities               (:entities session-context)
      :tr                     (:tr session-context)
      :tenant-schema          (:tenant-schema session-context)
      :format-date            date-picker/format-display-value})
    (decode-saved-view-settings (:named-view/settings named-view))))

(rf/reg-event-fx
 ::apply-saved-view
 [(rf/inject-cofx ::session-cofx/session-context)]
 (fn [{:keys [db session-context]} [_ {:keys [listing-id editor-id named-view]}]]
   (let [{:keys [view-config view-mode filters grid]}
         (view-settings {:named-view             named-view
                         :session-context        session-context
                         :accessible-field-paths (get-in (listing-runtime db listing-id)
                                                         [:context :accessible-field-paths])})
         view-id (:db/id named-view)
         updated-db (cond-> db
                      (map? view-config)
                      (update-in (model/listing-path listing-id) update :view-config merge view-config)
                      view-id
                      (assoc-in (conj (model/views-path listing-id) :active-id) view-id))]
     (maybe-persist-view-config! updated-db listing-id (get-in updated-db (model/view-config-path listing-id)))
     (maybe-persist-active-view-id! updated-db listing-id view-id)
     {:db updated-db
      :fx (saved-view-fx {:listing-id listing-id
                          :editor-id  editor-id
                          :view-mode  view-mode
                          :filters    filters
                          :sorted-by  (:sorted-by grid)
                          :runtime    (listing-runtime db listing-id)})})))

(defn- load-accessible-fields
  [{:keys [entity-type pipeline-id pipeline]}]
  (let [associated-entity-types (pipeline-entity/associated-entity-field->entity-types
                                 {:entity-type entity-type
                                  :pipeline pipeline})
        fields (os-access/accessible-dedicated-os-fields
                {:tenant                      @session/tenant
                 :tenant-schema               @session/tenant-schema
                 :account                     @session/account
                 :role                        @session/role
                 :account-current-permissions (:account/current-permissions @session/account)
                 :pipeline-id                 pipeline-id
                 :entity-type                 entity-type
                 :associated-entity-field->entity-types associated-entity-types})]
    {:accessible-fields      (vec fields)
     :accessible-field-paths (->> fields
                                  (map :field-path)
                                  set)}))

(defn- sanitize-field-paths
  [field-paths accessible-set]
  (->> field-paths
       (map (fn [field-path]
              (cond
                (vector? field-path) field-path
                (keyword? field-path) [field-path]
                (sequential? field-path) (vec field-path)
                :else nil)))
       (remove nil?)
       (filter accessible-set)
       distinct
       vec))

(defn- sanitize-active-filters
  [active-filters accessible-set]
  (->> active-filters
       (map (fn [{:keys [field-path] :as filter-item}]
              (let [normalized-field-path (cond
                                            (vector? field-path) field-path
                                            (keyword? field-path) [field-path]
                                            (sequential? field-path) (vec field-path)
                                            :else nil)]
                (when normalized-field-path
                  (assoc filter-item :field-path normalized-field-path)))))
       (remove nil?)
       (filter (fn [{:keys [field-path]}]
                 (contains? accessible-set field-path)))
       vec))

(defn- field-path->attribute
  [field-path]
  (cond
    (keyword? field-path) field-path
    (vector? field-path) (last field-path)
    (sequential? field-path) (last field-path)
    :else nil))

(defn- technical-or-derived-field-path?
  [field-path]
  (let [segments (when (sequential? field-path) (vec field-path))
        leaf (last segments)]
    (or (and (= 1 (count segments))
             (= leaf :common/display-name))
        (schema-system/hidden-system-field? field-path)
        (and (< 1 (count segments))
             (contains? #{:db/id :entity/type :common/display-name} leaf)))))

(defn- valid-group-by-field-path?
  [field-path]
  (cond
    (nil? field-path)
    true

    (keyword? field-path)
    (boolean (namespace field-path))

    (sequential? field-path)
    (let [segments (vec field-path)
          leaf (last segments)]
      (and (seq segments)
           (every? keyword? segments)
           (keyword? leaf)
           (boolean (namespace leaf))))

    :else
    false))

(defn- normalize-group-by-field-path
  [field-path]
  (let [normalize-segment (fn [segment]
                            (cond
                              (keyword? segment)
                              (if (namespace segment)
                                segment
                                (do
                                  (logging/warn "Group-by field segment is missing namespace"
                                                {:segment segment
                                                 :field-path field-path})
                                  nil))

                              (string? segment)
                              (let [normalized (if (string/starts-with? segment ":")
                                                 (subs segment 1)
                                                 segment)]
                                (if (string/includes? normalized "/")
                                  (keyword normalized)
                                  (do
                                    (logging/error "Group-by field string segment is missing namespace"
                                                   {:segment segment
                                                    :field-path field-path})
                                    nil)))

                              :else
                              (do
                                (logging/error "Group-by field segment has unsupported type"
                                               {:segment segment
                                                :segment-type (type segment)
                                                :field-path field-path})
                                nil)))
        normalized-path (cond
                          (nil? field-path)
                          nil

                          (keyword? field-path)
                          (when-let [segment (normalize-segment field-path)]
                            [segment])

                          (sequential? field-path)
                          (let [segments (vec field-path)
                                normalized-segments (mapv normalize-segment segments)]
                            (when (and (seq segments)
                                       (every? keyword? normalized-segments))
                              normalized-segments))

                          (string? field-path)
                          (when-let [segment (normalize-segment field-path)]
                            [segment])

                          :else
                          (do
                            (logging/error "Group-by field path has unsupported shape"
                                           {:field-path field-path
                                            :field-path-type (type field-path)})
                            nil))]
    (when (and (some? field-path)
               (nil? normalized-path))
      (logging/error "Failed to normalize group-by field path"
                     {:field-path field-path}))
    normalized-path))

(defn- resolve-default-group-by-field
  [entity-type groupable-field-paths]
  (let [configured-default (some-> entity-type
                                   listing-definition/kanban-group-by-field
                                   normalize-group-by-field-path)]
    (cond
      (contains? groupable-field-paths configured-default)
      configured-default

      (seq groupable-field-paths)
      (first (sort-by pr-str groupable-field-paths))

      :else
      nil)))

(defn- filters-payload
  [runtime]
  {:query   (get-in runtime [:query :text] "")
   :filters (get-in runtime [:filters :active] [])})

(defn- groupable-field?
  [field]
  (let [data-type (:field/data-type field)
        filter-type (:field/filter-type field)]
    (and (not (data-types/multi-data-type? data-type))
         (or (contains? facet-policy/select-value-data-types data-type)
             (contains? facet-policy/date-data-types data-type)
             (contains? facet-policy/numeric-data-types data-type)
             (and (= data-type :data.type/boolean)
                  (not= :filter.type/none filter-type))))))

(defn- field-data-type
  [accessible-fields field-path]
  (some (fn [{candidate-field-path :field-path
              data-type :field/data-type}]
          (when (= candidate-field-path field-path)
            data-type))
        accessible-fields))

(defn- numeric-group-field?
  [accessible-fields field-path]
  (contains? facet-policy/numeric-data-types
             (field-data-type accessible-fields field-path)))

(defn- numeric-sort-mode?
  [sort-mode]
  (contains? #{kanban-sort/numeric
               kanban-sort/numeric-desc}
             sort-mode))

(defn- default-sort-mode-for-group-field
  [{:keys [accessible-fields field-path sort-mode normalize-sort-mode default-sort-mode]}]
  (let [normalized-sort-mode (normalize-sort-mode sort-mode)]
    (cond
      (and (numeric-group-field? accessible-fields field-path)
           (= normalized-sort-mode default-sort-mode))
      kanban-sort/numeric

      (and (not (numeric-group-field? accessible-fields field-path))
           (numeric-sort-mode? normalized-sort-mode))
      default-sort-mode

      :else
      normalized-sort-mode)))

(defn- available-view-modes-for-session
  [available-view-modes]
  (disj (set (or available-view-modes #{:grid :kanban :gallery :list}))
        :gallery
        :list))

(defn- listen-for-index-completion
  "Another part of the UI can change the data that a listing shows from
  OpenSearch, for example the viewer of an entity. The backend rebuilds the
  search document and then sends a completion. The listing refreshes on it."
  [db listing-id]
  (completion-model/put-listener db {:listener-id   listing-id
                                     :kind          completion/search-index-kind
                                     :on-completion [::on-index-completion {:listing-id listing-id}]}))

(defn- stop-listening-for-index-completion
  [db listing-id]
  (completion-model/remove-listener db listing-id))

(defn on-index-completion-fx
  "Refresh the views of the listing. Both views read from OpenSearch, and the
  listing does not know which one is open. A view that is not open stays as it
  is."
  [_ [_ {:keys [listing-id]} _payload]]
  {:fx [[:dispatch [::grid-event/trigger-query-search (model/grid-id listing-id)]]
        [:dispatch [::kanban-event/refresh {:kanban-id (model/kanban-id listing-id)}]]]})

(rf/reg-event-fx
 ::on-index-completion
 on-index-completion-fx)

(rf/reg-event-fx
 ::init
 (fn [{:keys [db]} [_ {:keys [listing-id initial-view-mode]
                       :as opts}]]
   (let [available-modes (available-view-modes-for-session (:available-view-modes opts))
         runtime0 (model/default-runtime (assoc opts :available-view-modes available-modes))
         available-modes (get-in runtime0 [:params :available-view-modes] #{:kanban :gallery :list})
         persist-view-settings? (get-in runtime0 [:params :persist-view-settings?])
         preferred-view-mode (when persist-view-settings?
                               (persisted-view-mode listing-id))
         initial-view-mode (cond
                             (contains? available-modes preferred-view-mode)
                             preferred-view-mode

                             (contains? available-modes initial-view-mode)
                             initial-view-mode

                             :else
                             (get-in runtime0 [:view :mode]))
         runtime1 (assoc-in runtime0 [:view :mode] initial-view-mode)
         persisted-config (when persist-view-settings?
                            (persisted-view-config listing-id))
         runtime2 (if (map? persisted-config)
                    (update runtime1 :view-config merge persisted-config)
                    runtime1)
         persisted-filters-by-field-path (when persist-view-settings?
                                           (persisted-filters listing-id))
         runtime3 (if (map? persisted-filters-by-field-path)
                    (assoc-in runtime2 [:filters :persisted-by-field-path] persisted-filters-by-field-path)
                    runtime2)
         persisted-active-view-id (when persist-view-settings?
                                    (persisted-active-view-id listing-id))
         runtime4 (if persisted-active-view-id
                    (assoc-in runtime3 [:views :active-id] persisted-active-view-id)
                    runtime3)]
     {:db (-> db
              (assoc-in (model/listing-path listing-id) runtime4)
              (listen-for-index-completion listing-id))
      :fx [[:dispatch [::refresh-context listing-id]]]})))

(rf/reg-event-db
 ::destroy
 (fn [db [_ listing-id]]
   (-> db
       (update-in model/root-path dissoc listing-id)
       (stop-listening-for-index-completion listing-id))))

(rf/reg-event-db
 ::refresh-context
 (fn [db [_ listing-id]]
   (if-let [runtime (listing-runtime db listing-id)]
     (let [{:keys [entity-type pipeline-id pipeline]} (:params runtime)
           {:keys [accessible-fields]} (load-accessible-fields {:entity-type entity-type
                                                                :pipeline-id pipeline-id
                                                                :pipeline pipeline})
           tenant-schema @session/tenant-schema
           root-field-attributes (->> (registry/get-schema tenant-schema entity-type)
                                      keys
                                      set)
           accessible-fields (->> accessible-fields
                                  (keep (fn [{:keys [field-path short-label full-label] :as field}]
                                          (let [normalized-field-path (cond
                                                                        (vector? field-path) field-path
                                                                        (keyword? field-path) [field-path]
                                                                        (sequential? field-path) (vec field-path)
                                                                        :else nil)
                                                attribute (field-path->attribute normalized-field-path)
                                                field-schema (when (keyword? attribute)
                                                               (registry/get-field-schema tenant-schema attribute))
                                                localized-label (attribute/attribute-label tenant-schema normalized-field-path)
                                                path-field? (< 1 (count normalized-field-path))
                                                resolved-label (if path-field?
                                                                 ;; For deep-indexed path fields, prefer schema-driven
                                                                 ;; breadcrumb labels (attribute-label) so parent context
                                                                 ;; like "Investor - ..." is preserved.
                                                                 (or localized-label
                                                                     full-label
                                                                     (:label field)
                                                                     short-label)
                                                                 (or localized-label
                                                                     (:label field)
                                                                     short-label
                                                                     full-label))
                                                filter-type (or (:ui/default-filter-type field-schema)
                                                                (:field/filter-type field)
                                                                (get-in field-schema [:field/default-visibility :filter])
                                                                (:filter field-schema))]
                                            (when (and normalized-field-path
                                                       (or (not= 1 (count normalized-field-path))
                                                           (contains? root-field-attributes (last normalized-field-path)))
                                                       (not (technical-or-derived-field-path? normalized-field-path)))
                                              (assoc field
                                                     :field-path normalized-field-path
                                                     :field/attribute attribute
                                                     :label resolved-label
                                                     :field/name (or resolved-label
                                                                     (:field/name field)
                                                                     (some-> attribute name))
                                                     :field/data-type (or (:field/data-type field)
                                                                          (:field/data-type field-schema))
                                                     :field/filter-type filter-type
                                                     :field/options (or (:field/options field)
                                                                        (:options field-schema)
                                                                        [])
                                                     :field/schema field-schema)))))
                                  (reduce (fn [acc {:keys [field-path] :as field}]
                                            (if (or (nil? field-path)
                                                    (contains? (:seen acc) field-path))
                                              acc
                                              {:seen (conj (:seen acc) field-path)
                                               :items (conj (:items acc) field)}))
                                          {:seen #{}
                                           :items []})
                                  (:items)
                                  (sort-by (fn [{:keys [field-path label]}]
                                             [(if (= 1 (count field-path)) 0 1)
                                              (string/lower-case (or label ""))]))
                                  vec)
           accessible-field-paths (->> accessible-fields
                                       (map :field-path)
                                       set)
           groupable-field-paths (->> accessible-fields
                                      (filter groupable-field?)
                                      (map :field-path)
                                      set)
           po-defaults (get-in runtime [:params :po-default-field-paths] [])
           po-field-paths (sanitize-field-paths po-defaults accessible-field-paths)
           pinned-field-paths (sanitize-field-paths (get-in runtime [:filters :pinned-field-paths] []) accessible-field-paths)
           active-filters (sanitize-active-filters (get-in runtime [:filters :active] []) accessible-field-paths)
           requested-group-by-field (normalize-group-by-field-path
                                     (get-in runtime [:view-config :group-by-field]))
           requested-row-group-by-field (normalize-group-by-field-path
                                         (get-in runtime [:view-config :row-group-by-field]))
           requested-row-group-sort-mode (get-in runtime [:view-config :row-group-sort-mode])
           requested-kanban-column-sort-mode (get-in runtime [:view-config :kanban-column-sort-mode])
           requested-kanban-card-sort-mode (get-in runtime [:view-config :kanban-card-sort-mode])
           hide-empty-columns? (boolean (get-in runtime [:view-config :hide-empty-columns?]))
           default-group-by-field (resolve-default-group-by-field entity-type groupable-field-paths)
           sanitized-group-by-field (if (contains? groupable-field-paths requested-group-by-field)
                                      requested-group-by-field
                                      default-group-by-field)
           sanitized-row-group-by-field (when (contains? groupable-field-paths requested-row-group-by-field)
                                          requested-row-group-by-field)
           sanitized-row-group-sort-mode (default-sort-mode-for-group-field
                                          {:accessible-fields accessible-fields
                                           :field-path sanitized-row-group-by-field
                                           :sort-mode requested-row-group-sort-mode
                                           :normalize-sort-mode kanban-sort/normalize-row-sort-mode
                                           :default-sort-mode kanban-sort/default-row-sort-mode})
           sanitized-kanban-column-sort-mode (default-sort-mode-for-group-field
                                              {:accessible-fields accessible-fields
                                               :field-path sanitized-group-by-field
                                               :sort-mode requested-kanban-column-sort-mode
                                               :normalize-sort-mode kanban-sort/normalize-column-sort-mode
                                               :default-sort-mode kanban-sort/default-column-sort-mode})
           sanitized-kanban-card-sort-mode (kanban-sort/normalize-card-sort-mode requested-kanban-card-sort-mode)]
       #_(logging/info "KANBAN DND TRACE refresh-context group-by resolution"
                       {:listing-id listing-id
                        :entity-type entity-type
                        :pipeline-id pipeline-id
                        :requested-group-by-field (get-in runtime [:view-config :group-by-field])
                        :requested-group-by-field-pr (pr-str (get-in runtime [:view-config :group-by-field]))
                        :normalized-requested-group-by-field requested-group-by-field
                        :default-group-by-field default-group-by-field
                        :sanitized-group-by-field sanitized-group-by-field
                        :sanitized-group-by-field-pr (pr-str sanitized-group-by-field)
                        :group-by-attribute group-by-attribute
                        :group-by-field-schema-exists? (boolean group-by-field-schema)
                        :group-by-data-type group-by-data-type
                        :groupable-field-paths-count (count groupable-field-paths)})
       #_(js/console.error "KANBAN DND TRACE refresh-context group-by resolution"
                           (clj->js {:listing-id listing-id
                                     :entity-type entity-type
                                     :pipeline-id pipeline-id
                                     :requested-group-by-field (get-in runtime [:view-config :group-by-field])
                                     :requested-group-by-field-pr (pr-str (get-in runtime [:view-config :group-by-field]))
                                     :normalized-requested-group-by-field requested-group-by-field
                                     :default-group-by-field default-group-by-field
                                     :sanitized-group-by-field sanitized-group-by-field
                                     :sanitized-group-by-field-pr (pr-str sanitized-group-by-field)
                                     :group-by-attribute group-by-attribute
                                     :group-by-field-schema-exists? (boolean group-by-field-schema)
                                     :group-by-data-type group-by-data-type
                                     :groupable-field-paths-count (count groupable-field-paths)}))
       (-> db
           (assoc-in (conj (model/context-path listing-id) :account-id) @session/account-id)
           (assoc-in (conj (model/context-path listing-id) :account-current-permissions)
                     (:account/current-permissions @session/account))
           (assoc-in (conj (model/context-path listing-id) :accessible-fields) accessible-fields)
           (assoc-in (conj (model/context-path listing-id) :accessible-field-paths) accessible-field-paths)
           (assoc-in (conj (model/context-path listing-id) :groupable-field-paths) groupable-field-paths)
           (assoc-in (conj (model/filters-path listing-id) :po-field-paths) po-field-paths)
           (assoc-in (conj (model/filters-path listing-id) :pinned-field-paths) pinned-field-paths)
           (assoc-in (conj (model/filters-path listing-id) :active) active-filters)
           (assoc-in (conj (model/view-config-path listing-id) :group-by-field) sanitized-group-by-field)
           (assoc-in (conj (model/view-config-path listing-id) :row-group-by-field) sanitized-row-group-by-field)
           (assoc-in (conj (model/view-config-path listing-id) :row-group-sort-mode) sanitized-row-group-sort-mode)
           (assoc-in (conj (model/view-config-path listing-id) :kanban-column-sort-mode) sanitized-kanban-column-sort-mode)
           (assoc-in (conj (model/view-config-path listing-id) :kanban-card-sort-mode) sanitized-kanban-card-sort-mode)
           (assoc-in (conj (model/view-config-path listing-id) :hide-empty-columns?) hide-empty-columns?)
           (assoc-in (conj (model/ui-path listing-id) :initialized?) true)))
     db)))

(rf/reg-event-fx
 ::set-view-mode
 (fn [{:keys [db]} [_ listing-id mode]]
   (let [runtime (listing-runtime db listing-id)
         allowed? (contains? (get-in runtime [:params :available-view-modes] #{}) mode)
         next-mode (if allowed?
                     mode
                     (get-in runtime [:view :mode]))
         updated-db (-> db
                        (assoc-in (conj (model/view-path listing-id) :previous-mode) (get-in runtime [:view :mode]))
                        (assoc-in (conj (model/view-path listing-id) :mode) next-mode))
         updated-runtime (listing-runtime updated-db listing-id)]
     (when allowed?
       (maybe-persist-view-mode! updated-db listing-id next-mode))
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-view-mode-changed-event
                              {:listing-id listing-id
                               :mode       next-mode})})))

(rf/reg-event-fx
 ::set-query-draft
 (fn [{:keys [db]} [_ listing-id value]]
   {:db               (assoc-in db (conj (model/query-path listing-id) :draft) (or value ""))
    ::timeout/timeout {:id      (keyword (str "generic-listing-query-" (pr-str listing-id)))
                       :event   [::commit-query listing-id (or value "")]
                       :wait-ms (get-in db (conj (model/query-path listing-id) :debounce-ms) 300)}}))

(rf/reg-event-fx
 ::commit-query
 (fn [{:keys [db]} [_ listing-id value]]
   (let [updated-db (assoc-in db (conj (model/query-path listing-id) :text) (or value ""))
         updated-runtime (listing-runtime updated-db listing-id)]
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-filters-changed-event
                              (filters-payload updated-runtime))})))

(rf/reg-event-fx
 ::set-active-filters
 (fn [{:keys [db]} [_ listing-id active-filters]]
   (let [accessible-set (get-in db (conj (model/context-path listing-id) :accessible-field-paths) #{})
         sanitized (sanitize-active-filters (vec (or active-filters [])) accessible-set)
         updated-db (assoc-in db (conj (model/filters-path listing-id) :active) sanitized)
         updated-runtime (listing-runtime updated-db listing-id)]
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-filters-changed-event
                              (filters-payload updated-runtime))})))

(rf/reg-event-fx
 ::upsert-active-filter
 (fn [{:keys [db]} [_ listing-id new-filter]]
   (let [filters-path (conj (model/filters-path listing-id) :active)
         current (get-in db filters-path [])
         next-filters (->> current
                           (remove (fn [f]
                                     (= (:field-path f) (:field-path new-filter))))
                           (cons new-filter)
                           vec)]
     {:fx [[:dispatch [::set-active-filters listing-id next-filters]]]})))

(rf/reg-event-fx
 ::remove-active-filter
 (fn [{:keys [db]} [_ listing-id field-path]]
   (let [filters (get-in db (conj (model/filters-path listing-id) :active) [])
         next-filters (->> filters
                           (remove (fn [f] (= (:field-path f) field-path)))
                           vec)]
     {:fx [[:dispatch [::set-active-filters listing-id next-filters]]]})))

(rf/reg-event-fx
 ::clear-filters
 (fn [_ [_ listing-id]]
   {:fx [[:dispatch [::set-active-filters listing-id []]]]}))

(rf/reg-event-fx
 ::persist-filters
 (fn [{:keys [db]} [_ listing-id {:keys [filters-by-field-path]}]]
   (maybe-persist-filters! db listing-id filters-by-field-path)
   {}))

(rf/reg-event-fx
 ::set-group-by-field
 (fn [{:keys [db]} [_ listing-id field-path]]
   (let [normalized-field-path (normalize-group-by-field-path field-path)
         groupable-field-paths (get-in db (conj (model/context-path listing-id) :groupable-field-paths) #{})
         accessible-fields (get-in db (conj (model/context-path listing-id) :accessible-fields) [])
         entity-type (get-in db (conj (model/params-path listing-id) :entity-type))
         default-group-by-field (resolve-default-group-by-field entity-type groupable-field-paths)
         resolved-group-by-field (if (contains? groupable-field-paths normalized-field-path)
                                   normalized-field-path
                                   default-group-by-field)]
     #_(logging/info "KANBAN DND TRACE set-group-by-field"
                     {:listing-id listing-id
                      :incoming-field-path field-path
                      :incoming-field-path-pr (pr-str field-path)
                      :incoming-field-path-type (str (type field-path))
                      :normalized-field-path normalized-field-path
                      :default-group-by-field default-group-by-field
                      :resolved-group-by-field resolved-group-by-field
                      :normalized-field-path-pr (pr-str normalized-field-path)
                      :normalized-field-path-type (str (type normalized-field-path))
                      :valid? (valid-group-by-field-path? normalized-field-path)})
     #_(js/console.error "KANBAN DND TRACE set-group-by-field"
                         (clj->js {:listing-id listing-id
                                   :incoming-field-path field-path
                                   :incoming-field-path-pr (pr-str field-path)
                                   :incoming-field-path-type (str (type field-path))
                                   :normalized-field-path normalized-field-path
                                   :default-group-by-field default-group-by-field
                                   :resolved-group-by-field resolved-group-by-field
                                   :normalized-field-path-pr (pr-str normalized-field-path)
                                   :normalized-field-path-type (str (type normalized-field-path))
                                   :valid? (valid-group-by-field-path? normalized-field-path)}))
     (if (valid-group-by-field-path? normalized-field-path)
       (let [current-sort-mode (get-in db (conj (model/view-config-path listing-id) :kanban-column-sort-mode))
             updated-sort-mode (default-sort-mode-for-group-field
                                {:accessible-fields accessible-fields
                                 :field-path resolved-group-by-field
                                 :sort-mode current-sort-mode
                                 :normalize-sort-mode kanban-sort/normalize-column-sort-mode
                                 :default-sort-mode kanban-sort/default-column-sort-mode})
             updated-db (-> db
                            (assoc-in (conj (model/view-config-path listing-id) :group-by-field)
                                      resolved-group-by-field)
                            (assoc-in (conj (model/view-config-path listing-id) :kanban-column-sort-mode)
                                      updated-sort-mode))
             updated-view-config (get-in updated-db (model/view-config-path listing-id))]
         (maybe-persist-view-config! updated-db listing-id updated-view-config)
         {:db updated-db})
       (do (logging/error "Rejecting invalid group-by-field path in listing config"
                          {:listing-id listing-id
                           :field-path field-path
                           :normalized-field-path normalized-field-path})
           {:db db})))))

(rf/reg-event-fx
 ::clear-group-by-field
 (fn [{:keys [db]} [_ listing-id]]
   (let [updated-db (assoc-in db (conj (model/view-config-path listing-id) :group-by-field) nil)
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::set-row-group-by-field
 (fn [{:keys [db]} [_ listing-id field-path]]
   (let [normalized-field-path (normalize-group-by-field-path field-path)
         groupable-field-paths (get-in db (conj (model/context-path listing-id) :groupable-field-paths) #{})
         accessible-fields (get-in db (conj (model/context-path listing-id) :accessible-fields) [])
         resolved-row-group-by-field (when (contains? groupable-field-paths normalized-field-path)
                                       normalized-field-path)]
     (if (valid-group-by-field-path? normalized-field-path)
       (let [current-sort-mode (get-in db (conj (model/view-config-path listing-id) :row-group-sort-mode))
             updated-sort-mode (default-sort-mode-for-group-field
                                {:accessible-fields accessible-fields
                                 :field-path resolved-row-group-by-field
                                 :sort-mode current-sort-mode
                                 :normalize-sort-mode kanban-sort/normalize-row-sort-mode
                                 :default-sort-mode kanban-sort/default-row-sort-mode})
             updated-db (-> db
                            (assoc-in (conj (model/view-config-path listing-id) :row-group-by-field)
                                      resolved-row-group-by-field)
                            (assoc-in (conj (model/view-config-path listing-id) :row-group-sort-mode)
                                      updated-sort-mode))
             updated-view-config (get-in updated-db (model/view-config-path listing-id))]
         (maybe-persist-view-config! updated-db listing-id updated-view-config)
         {:db updated-db})
       (do (logging/error "Rejecting invalid row-group-by-field path in listing config"
                          {:listing-id listing-id
                           :field-path field-path
                           :normalized-field-path normalized-field-path})
           {:db db})))))

(rf/reg-event-fx
 ::clear-row-group-by-field
 (fn [{:keys [db]} [_ listing-id]]
   (let [updated-db (assoc-in db (conj (model/view-config-path listing-id) :row-group-by-field) nil)
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::set-row-group-sort-mode
 (fn [{:keys [db]} [_ listing-id sort-mode]]
   (let [updated-db (assoc-in db
                              (conj (model/view-config-path listing-id) :row-group-sort-mode)
                              (kanban-sort/normalize-row-sort-mode sort-mode))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::set-hide-empty-columns
 (fn [{:keys [db]} [_ listing-id hide-empty-columns?]]
   (let [updated-db (assoc-in db
                              (conj (model/view-config-path listing-id) :hide-empty-columns?)
                              (boolean hide-empty-columns?))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(defn set-grid-column-filters-fx
  "Keep the column filters of the grid in the listing. The grid is destroyed when
   the user changes to another view mode, and the listing is not, so the next grid
   can get the filters back. `filter-model` is the model of AG Grid with string
   keys. A model that is nil or empty clears the filters. The event does nothing
   for a listing that has no runtime, as a grid can send it while the listing is
   destroyed, and the event must not make a runtime again."
  [{:keys [db]} [_ listing-id filter-model]]
  (when (get-in db (model/listing-path listing-id))
    {:db (assoc-in db (model/grid-column-filters-path listing-id) (or filter-model {}))}))

(rf/reg-event-fx ::set-grid-column-filters set-grid-column-filters-fx)

(defn restore-grid-column-filters-fx
  "Give the column filters that the listing keeps to a grid that is new, when the
   grid is ready. `api` is the AG Grid api of the new grid. The listing has no
   filters to give at the first start of the grid, and then the event does
   nothing."
  [{:keys [db]} [_ listing-id api]]
  (let [filter-model (get-in db (model/grid-column-filters-path listing-id))]
    (when (seq filter-model)
      {:fx [[::grid-event/apply-filter-model {:api          api
                                              :filter-model filter-model}]]})))

(rf/reg-event-fx ::restore-grid-column-filters restore-grid-column-filters-fx)

(rf/reg-event-fx
 ::set-kanban-column-sort-mode
 (fn [{:keys [db]} [_ listing-id sort-mode]]
   (let [updated-db (assoc-in db
                              (conj (model/view-config-path listing-id) :kanban-column-sort-mode)
                              (kanban-sort/normalize-column-sort-mode sort-mode))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::set-kanban-card-sort-mode
 (fn [{:keys [db]} [_ listing-id sort-mode]]
   (let [updated-db (assoc-in db
                              (conj (model/view-config-path listing-id) :kanban-card-sort-mode)
                              (kanban-sort/normalize-card-sort-mode sort-mode))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::set-card-show-labels
 (fn [{:keys [db]} [_ listing-id view-mode show-labels?]]
   (let [updated-db (assoc-in db
                              (conj (model/view-config-path listing-id)
                                    :card :by-view-mode view-mode :show-labels?)
                              (boolean show-labels?))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::toggle-card-body-field
 (fn [{:keys [db]} [_ listing-id view-mode field-path effective-body-fields]]
   (let [path (conj (model/view-config-path listing-id)
                    :card :by-view-mode view-mode :body-fields)
         current (get-in db path)
         base-fields (vec (or (when (seq current) current)
                              effective-body-fields
                              []))
         selected? (some (fn [p] (= p field-path)) base-fields)
         next-fields (if selected?
                       (->> base-fields
                            (remove (fn [p] (= p field-path)))
                            vec)
                       (conj base-fields field-path))
         updated-db (assoc-in db path next-fields)
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-db
 ::set-filter-manage-open
 (fn [db [_ listing-id open?]]
   (assoc-in db (conj (model/filters-path listing-id) :manage-open?) (boolean open?))))

(rf/reg-event-db
 ::toggle-filter-manage-open
 (fn [db [_ listing-id]]
   (update-in db (conj (model/filters-path listing-id) :manage-open?) not)))

(rf/reg-event-db
 ::set-selected-filter-field
 (fn [db [_ listing-id field-path]]
   (assoc-in db (conj (model/filters-path listing-id) :selected-field-path) field-path)))

(rf/reg-event-db
 ::set-pinned-field-paths
 (fn [db [_ listing-id field-paths]]
   (let [accessible-set (get-in db (conj (model/context-path listing-id) :accessible-field-paths) #{})
         pinned (sanitize-field-paths (vec (or field-paths [])) accessible-set)]
     (assoc-in db (conj (model/filters-path listing-id) :pinned-field-paths) pinned))))

(rf/reg-event-db
 ::set-selected-ids
 (fn [db [_ listing-id selected-ids]]
   (update-in db (model/selection-path listing-id) selection/ids-selection selected-ids)))

(rf/reg-event-db
 ::set-all-selection
 (fn [db [_ listing-id args]]
   (update-in db (model/selection-path listing-id) selection/all-selection args)))

(rf/reg-event-fx
 ::toggle-selected-id
 (fn [{:keys [db]} [_ listing-id item-id]]
   (let [selected (get-in db (conj (model/selection-path listing-id) :selected-ids) #{})
         next-selected (if (contains? selected item-id)
                         (disj selected item-id)
                         (conj selected item-id))
         updated-db (-> db
                        (update-in (model/selection-path listing-id) selection/ids-selection next-selected)
                        (assoc-in (conj (model/selection-path listing-id) :last-selected-id) item-id))
         updated-runtime (listing-runtime updated-db listing-id)]
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-selection-changed-event
                              {:selected-ids (vec next-selected)
                               :last-selected-id item-id})})))

(defn clear-selection-fx
  "Clear the selection. The grid keeps its own selection state, so a selection of
   all rows also clears the grid. Otherwise the grid would show rows as selected
   that the listing does not know."
  [{:keys [db]} [_ listing-id]]
  (let [current (get-in db (model/selection-path listing-id))
        grid-id (when (selection/all-selection? current)
                  (:grid-id current))
        updated-db (-> db
                       (update-in (model/selection-path listing-id) selection/ids-selection #{})
                       (assoc-in (conj (model/selection-path listing-id) :last-selected-id) nil))
        updated-runtime (listing-runtime updated-db listing-id)]
    {:db updated-db
     :fx (cond-> (append-callback-fx []
                                     updated-runtime
                                     :on-selection-changed-event
                                     {:selected-ids []
                                      :last-selected-id nil})
           (some? grid-id)
           (conj [:dispatch [::grid-event/clear-grid-selection grid-id]]))}))

(rf/reg-event-fx ::clear-selection clear-selection-fx)

(defn with-resolved-selection-fx
  "Run the event `then` for an action that works on the selected rows. The
   action reads the ids with `selection/action-ids`. For a selection of all rows
   the ids are not known yet, so this asks the grid for them first. A selection
   that is too large gives an error and `then` does not run."
  [{:keys [db]} [_ {:keys [listing-id then]}]]
  (let [current (get-in db (model/selection-path listing-id))
        grid-id (:grid-id current)
        expected-count (selection/selected-count
                        current
                        (get-in db (grid-model/last-query-total-full-path grid-id)))]
    (cond
      (not (selection/all-selection? current))
      {:fx [[:dispatch then]]}

      (selection/over-limit? expected-count)
      {::toast/error (loc/tr :select-all-limit-exceeded [selection/max-all-selection-count])}

      :else
      {:fx [[:dispatch [::grid-event/fetch-all-ids
                        grid-id
                        {:on-ok    [::all-ids-fetched {:listing-id     listing-id
                                                       :then           then
                                                       :expected-count expected-count}]
                         :on-error [::all-ids-failed]}]]]})))

(rf/reg-event-fx ::with-resolved-selection with-resolved-selection-fx)

(defn all-ids-fetched-fx
  "The grid returned the ids of all its rows. The user may have cleared the
   selection while the request ran. Then the action does not run."
  [{:keys [db]} [_ {:keys [listing-id then expected-count]} fetched-ids]]
  (let [current (get-in db (model/selection-path listing-id))]
    (if (selection/all-selection? current)
      {:db (update-in db
                      (model/selection-path listing-id)
                      selection/store-resolved
                      (selection/resolve-ids {:fetched-ids   fetched-ids
                                              :excluded-ids (:excluded-ids current)})
                      expected-count)
       :fx [[:dispatch then]]}
      {})))

(rf/reg-event-fx ::all-ids-fetched all-ids-fetched-fx)

(rf/reg-event-fx
 ::all-ids-failed
 (fn [_ [_ err]]
   (logging/error "Unable to load the ids of the selected rows" err)
   {::toast/error (loc/tr :select-all-resolve-failed)}))

(rf/reg-event-fx
 ::row-clicked
 (fn [{:keys [db]} [_ listing-id row]]
   (let [runtime (listing-runtime db listing-id)]
     {:fx (append-callback-fx [] runtime :on-row-click-event row)})))

(rf/reg-event-fx
 ::item-clicked
 (fn [{:keys [db]} [_ listing-id item]]
   (let [runtime (listing-runtime db listing-id)]
     {:fx (append-callback-fx [] runtime :on-item-click-event item)})))

(rf/reg-event-fx
 ::trigger-toolbar-action
 (fn [{:keys [db]} [_ listing-id action-id payload]]
   (let [runtime (listing-runtime db listing-id)
         action (->> (get-in runtime [:toolbar :actions] [])
                     (filter (fn [{:keys [id]}] (= id action-id)))
                     first)
         action-event (:event action)
         fx (cond-> []
              (vector? action-event)
              (conj [:dispatch (conj (vec action-event)
                                     (merge {:action-id action-id
                                             :listing-id listing-id}
                                            (or payload {})))])

              true
              (into (append-callback-fx []
                                        runtime
                                        :on-selection-changed-event
                                        {:selected-ids (vec (get-in runtime [:selection :selected-ids] #{}))
                                         :action-id action-id
                                         :payload payload})))]
     {:fx fx})))

(rf/reg-event-fx
 ::set-view-config
 (fn [{:keys [db]} [_ listing-id patch]]
   (let [updated-db (-> db
                        (update-in (model/view-config-path listing-id) merge patch)
                        (assoc-in (conj (model/view-config-path listing-id) :dirty?) true))
         updated-view-config (get-in updated-db (model/view-config-path listing-id))]
     (maybe-persist-view-config! updated-db listing-id updated-view-config)
     {:db updated-db})))

(rf/reg-event-fx
 ::reset-view-config
 (fn [{:keys [db]} [_ listing-id]]
   (if-let [runtime (listing-runtime db listing-id)]
     (let [entity-type (get-in runtime [:params :entity-type])
           groupable-field-paths (get-in runtime [:context :groupable-field-paths] #{})
           group-by-field (resolve-default-group-by-field entity-type groupable-field-paths)
           reset-view-config (assoc (get (model/default-runtime {:listing-id             listing-id
                                                                 :entity-type            entity-type
                                                                 :pipeline-id            (get-in runtime [:params :pipeline-id])
                                                                 :po-default-field-paths (get-in runtime [:params :po-default-field-paths])
                                                                 :available-view-modes   (get-in runtime [:params :available-view-modes])
                                                                 :toolbar-actions        (get-in runtime [:toolbar :actions])
                                                                 :callbacks              (:callbacks runtime)
                                                                 :filters                (get-in runtime [:params :filters])})
                                         :view-config)
                                    :group-by-field group-by-field
                                    :row-group-by-field nil)
           updated-db (assoc-in db
                                (model/view-config-path listing-id)
                                reset-view-config)]
       (ls/set-in (ls-path listing-id :view-config) nil)
       (ls/set-in (ls-path listing-id :saved-view) nil)
       (ls/set-in (ls-path listing-id :view-mode) nil)
       {:db updated-db
        :fx [[:dispatch [::grid-event/reset-config (str listing-id "-grid")]]
             [:dispatch [::refresh-context listing-id]]]})
     {:db db})))

(rf/reg-event-fx
 ::save-view-config
 (fn [{:keys [db]} [_ listing-id]]
   (let [updated-db (assoc-in db (conj (model/view-config-path listing-id) :dirty?) false)
         updated-runtime (listing-runtime updated-db listing-id)]
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-view-config-saved-event
                              (get-in updated-runtime [:view-config]))})))

(rf/reg-event-fx
 ::share-view-config
 (fn [{:keys [db]} [_ listing-id]]
   (let [updated-db (assoc-in db (conj (model/view-config-path listing-id) :shared?) true)
         updated-runtime (listing-runtime updated-db listing-id)]
     {:db updated-db
      :fx (append-callback-fx []
                              updated-runtime
                              :on-view-config-shared-event
                              (get-in updated-runtime [:view-config]))})))

(rf/reg-event-db
 ::set-ui-busy
 (fn [db [_ listing-id busy? busy-reason]]
   (-> db
       (assoc-in (conj (model/ui-path listing-id) :busy?) (boolean busy?))
       (assoc-in (conj (model/ui-path listing-id) :busy-reason) busy-reason))))

(rf/reg-event-db
 ::set-ui-busy-key
 (fn [db [_ listing-id busy-key busy?]]
   (assoc-in db (conj (model/ui-path listing-id) :busy-by-key busy-key) (boolean busy?))))

(rf/reg-event-db
 ::set-ui-disabled-key
 (fn [db [_ listing-id control-key disabled?]]
   (assoc-in db (conj (model/ui-path listing-id) :disabled-by-key control-key) (boolean disabled?))))
